"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Flame, ArrowRight, Gauge, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function HazardousHero() {
  const shouldReduceMotion = useReducedMotion();
  const keySpecs = [{
    label: "Zone 1 / 21 & Zone 2 / 22 Certified"
  }, {
    label: "ATEX & IECEx Compliant"
  }, {
    label: "Real-Time In-Line Monitoring"
  }];
  return <section id="hazardousareahero" data-nav="dark" className="relative overflow-hidden bg-sidebar text-sidebar-foreground py-16 lg:py-24">
      {/* Subtle industrial background glow & grid */}
      <div className="pointer-events-none absolute inset-0 opacity-10">
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-primary blur-3xl" />
        <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-chart-3 blur-3xl" />
      </div>

      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          
          {/* Left Column: Core Copy & CTAs */}
          <motion.div initial={shouldReduceMotion ? {} : {
          opacity: 0,
          y: 20
        }} animate={shouldReduceMotion ? {} : {
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="flex flex-col items-start lg:col-span-7">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="default" className="bg-primary text-primary-foreground font-semibold px-3 py-1 text-xs tracking-wider uppercase flex items-center gap-1.5 shadow-sm">
                <Flame className="size-3.5" />
                Explosion-Proof Systems
              </Badge>
              <Badge variant="outline" className="border-sidebar-border text-sidebar-foreground/90 bg-sidebar-accent/50 px-3 py-1 text-xs">
                <ShieldCheck className="size-3.5 text-primary" />
                Certified Hazardous Zone Ready
              </Badge>
            </div>

            <h1 className="text-3xl font-bold tracking-tight text-sidebar-foreground sm:text-4xl md:text-5xl lg:leading-tight">
              Hazardous-Area Process Analysis Solutions
            </h1>

            <p className="mt-5 text-base sm:text-lg text-sidebar-foreground/80 leading-relaxed max-w-2xl">
              Specialized analytical instrumentation engineered for classified environments.
              Ensure rigorous safety compliance, continuous operational uptime, and dependable process control across pharmaceutical and chemical manufacturing.
            </p>

            {/* Value checklist */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
              {keySpecs.map((spec, i) => <div key={spec.label} data-index={i} className="flex items-center gap-2 text-sm text-sidebar-foreground/90">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>{spec.label}</span>
                </div>)}
            </div>

            {/* Action buttons */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-semibold">
                <Link to="/DiscussYourApplicationPage" className="inline-flex items-center gap-2">
                  Discuss Your Application
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-sidebar-border bg-sidebar-accent/60 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground font-semibold">
                <Link to="/RequestAQuotePage">Request a Quote</Link>
              </Button>
            </div>

            {/* Sub-navigation links */}
            <div className="mt-8 pt-6 border-t border-sidebar-border/60 flex flex-wrap items-center gap-6 text-xs text-sidebar-foreground/70">
              <span className="font-medium text-sidebar-foreground/90">Explore related categories:</span>
              <Link to="/ProcessAnalyticalTechnologyPage" className="hover:text-primary transition-colors underline-offset-4 hover:underline">
                Process Analytical Technology (PAT)
              </Link>
              <Link to="/SolutionsPage" className="hover:text-primary transition-colors underline-offset-4 hover:underline">
                View All Solutions
              </Link>
            </div>
          </motion.div>

          {/* Right Column: Hero Graphic / Equipment Display */}
          <motion.div initial={shouldReduceMotion ? {} : {
          opacity: 0,
          scale: 0.96
        }} animate={shouldReduceMotion ? {} : {
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          ease: "easeOut",
          delay: 0.1
        }} className="lg:col-span-5">
            <div className="relative rounded-2xl border border-sidebar-border bg-sidebar-accent/30 p-2 shadow-2xl backdrop-blur-sm">
              <div className="relative overflow-hidden rounded-xl bg-sidebar-border/30 aspect-4/3 sm:aspect-16/10 lg:aspect-4/3">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/hazardous_area_process_analyzer.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/hazardous_area_process_analyzer.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/hazardous_area_process_analyzer.png-wvc-sizes" alt="Hazardous area certified analytical spectrometer installed on an industrial process line" loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />

                {/* Overlaid stat pill */}
                <div className="absolute bottom-3 left-3 right-3 rounded-lg border border-sidebar-border/80 bg-sidebar/90 p-3 backdrop-blur-md shadow-lg flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex size-9 items-center justify-center rounded-md bg-primary/20 text-primary">
                      <Gauge className="size-5" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-sidebar-foreground">Zone 1 / ATEX Certified</p>
                      <p className="text-[11px] text-sidebar-foreground/70">Ruggedized Process Housing</p>
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-[11px] bg-sidebar-accent text-sidebar-foreground border-sidebar-border">
                    Class I, Div 1
                  </Badge>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}